<template>
	<div role="region" 
		 aria-labelledby="scratch-game-heading"
		 @keydown.space.prevent="triggerScratch"
		 @keydown.enter="triggerScratch">
		
		<h3 id="scratch-game-heading" class="sr-only">{{ $t('Accessibility.ScratchAndWin.Title') }}</h3>
		
		<!-- Game instructions for screen readers -->
		<div class="sr-only" aria-live="polite" id="scratch-instructions">
			{{ gameInstructions }}
		</div>

		<!-- Current game state announcement -->
		<div class="sr-only" aria-live="polite" id="scratch-status">
			{{ gameStatus }}
		</div>

		<scratch-card :backUrl="data.mainVisualUrl"
					  :finishPercent="data.revealPercentage"
					  :imageUrl="data.scratchVisualUrl"
					  :isDemo="isDemo"
					  @onComplete="onComplete"
					  @onPercentageComplete="onPercentageComplete"
					  ref="scratchCard"
					  v-if="data"
					  role="application"
					  :aria-label="scratchAriaLabel">
		</scratch-card>

		<div class="h5 text-center mt-3 blink_me" 
			 :style="keepScratchingTextStyle()"
			 v-if="!completed"
			 role="status"
			 aria-live="polite">
			{{ data.keepScratchingText }}
		</div>

		<!-- Keyboard instructions -->
		<div class="sr-only" v-if="!started">
			{{ $t('Accessibility.ScratchAndWin.KeyboardInstructions') }}
		</div>
	</div>

</template>

<script>
    import ScratchCard from './subcomponents/scratch-card';

    export default {
        props: {
            data: {},
            fontFamily: null,
            fontSize: null,
            isDemo: false
        },
        components: {
            ScratchCard,
        },
        data() {
            return {
                completed: false,
				started: false,
				scratchPercentage: 0,
				gameStatus: ''
            };
        },
		computed: {
			gameInstructions() {
				return this.$t('Accessibility.ScratchAndWin.Instructions');
			},
			scratchAriaLabel() {
				if (this.completed) {
					return this.$t('Accessibility.ScratchAndWin.AriaLabelCompleted');
				} else if (this.started) {
					return this.$t('Accessibility.ScratchAndWin.AriaLabelProgress', { percentage: this.scratchPercentage });
				} else {
					return this.$t('Accessibility.ScratchAndWin.AriaLabelReady');
				}
			}
		},
		mounted() {
			// Set initial game status
			this.gameStatus = this.$t('Accessibility.ScratchAndWin.Ready');
		},
        methods: {
            keepScratchingTextStyle: function() {
              let style = {
                color: this.data.keepScratchingTextColor,
              }

              if(this.fontFamily)
                style.fontFamily = this.fontFamily;
              if(this.fontSize)
                style.fontSize = this.fontSize + 'px';
              return style;
            },
            onComplete: function (event) {
                this.completed = true;
				this.gameStatus = this.$t('Accessibility.ScratchAndWin.Completed');
                // `this` inside methods points to the Vue instance
                this.$emit('end', event);
            },
            onPercentageComplete: function (event) {
                if(!this.started) {
                    this.started = true;
					this.gameStatus = this.$t('Accessibility.ScratchAndWin.Started');
                    this.$emit('start');
				}
				
				// Update scratch percentage for accessibility
				this.scratchPercentage = Math.round(event.percentage || 0);
				
				// Announce progress at key milestones
				if (this.scratchPercentage === 25 || this.scratchPercentage === 50 || this.scratchPercentage === 75) {
					this.gameStatus = this.$t('Accessibility.ScratchAndWin.Progress', { percentage: this.scratchPercentage });
				}
				
                if (this.isDemo) {
                    this.$emit('percentage', event);
                }
            },
			
			triggerScratch() {
				// Keyboard accessibility - simulate scratching
				if (!this.started && !this.completed && this.$refs.scratchCard) {
					// Try to trigger the scratch card's start method if available
					if (this.$refs.scratchCard.startScratching) {
						this.$refs.scratchCard.startScratching();
					} else if (this.$refs.scratchCard.$el) {
						// Fallback: simulate a click/touch event
						const canvas = this.$refs.scratchCard.$el.querySelector('canvas');
						if (canvas) {
							// Simulate scratching by triggering mouse events
							const rect = canvas.getBoundingClientRect();
							const centerX = rect.width / 2;
							const centerY = rect.height / 2;
							
							// Create and dispatch mouse events to simulate scratching
							const mouseDown = new MouseEvent('mousedown', {
								clientX: rect.left + centerX,
								clientY: rect.top + centerY,
								bubbles: true
							});
							const mouseMove = new MouseEvent('mousemove', {
								clientX: rect.left + centerX + 50,
								clientY: rect.top + centerY + 50,
								bubbles: true
							});
							const mouseUp = new MouseEvent('mouseup', {
								bubbles: true
							});
							
							canvas.dispatchEvent(mouseDown);
							setTimeout(() => canvas.dispatchEvent(mouseMove), 50);
							setTimeout(() => canvas.dispatchEvent(mouseUp), 100);
						}
					}
				}
			}
        }
    };

</script>

<style scoped>
	/* Screen reader only content */
	.sr-only {
		position: absolute !important;
		width: 1px !important;
		height: 1px !important;
		padding: 0 !important;
		margin: -1px !important;
		overflow: hidden !important;
		clip: rect(0, 0, 0, 0) !important;
		white-space: nowrap !important;
		border: 0 !important;
	}

	/* Hide focus outline for visual users while maintaining accessibility */
	div[role="region"]:focus {
		outline: none;
	}

	.card-content {
		color: red;
	}

	.blink_me {
		animation: blinker 1s linear infinite;
	}

	@keyframes blinker {
		50% {
			opacity: 0;
		}
	}

</style>
